import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './RecordList.module.css';
import ImageWithFallback from '../GenericComponents/ImageWithFallback';
import * as ccUtils from '../CC/utils';
import { useCCContext } from '../CC/CCContext';
import { getImages } from '../Result/getImages';
import DOMPurify from 'dompurify';

interface RecordListProps {
    record: any;
    index: number;
    handleLinkClick: (event: React.MouseEvent, index: number) => void;
}

const RecordList: React.FC<RecordListProps> = ({ record, index, handleLinkClick }) => {
    const { t } = useTranslation();
    const ctx = useCCContext();

    function getDefaultRow(record: any, label: string, fieldName: string, defaultValue: string = "") {
        const fieldValue = record?.data?.Record?.[fieldName];

        // If fieldValue doesn't exist and no defaultValue is provided, return null
        if (!fieldValue && !defaultValue) return null;

        return (
            <div className={styles.row}>
                <span className={styles.taglabel}>{label} : </span>
                {fieldValue ?? defaultValue}
            </div>
        );
    }

    function getDefaultRowHyperlink(record: any, label: string, fieldName: string, subtag: string = "", defaultValue: string = "") {

        let fieldValue = record?.data?.Record?.[fieldName];
        if (subtag && fieldValue) {
            fieldValue = fieldValue[subtag] || defaultValue;
        }

        const handleOnClick = (label: string, value: string, e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            //geen volledige veldwaarde zoeken? Medium is geen browsetag.
            if (!subtag) {
                ccUtils.customSearch(ctx, '/Record/' + fieldName, '', '' + value + '', label + ' : ' + value);
            } else {
                ccUtils.customSearch(ctx, '/Record/' + fieldName + '/' + subtag, '', '' + value + '', label + ' : ' + value);
            }
        };

        // If fieldValue doesn't exist and no defaultValue is provided, return null
        if (!fieldValue && !defaultValue) return null;

        return (
            <div className={styles.row}>
                <span className={styles.taglabel}>{label} : </span>
                {fieldValue
                    ? <span className={styles.hyperlink} onClick={(e) => handleOnClick(label, fieldValue, e)}>{fieldValue}</span>
                    : defaultValue}
            </div>
        );
    }

    function getDefaultRowHyperlinkHtml(record: any, label: string, fieldName: string, subtag: string = "", defaultValue: string = "") {

        let fieldValue = record?.data?.Record?.[fieldName];
        if (subtag && fieldValue) {
            fieldValue = fieldValue[subtag] || defaultValue;
        }

        const handleOnClick = (label: string, value: string, e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            //geen volledige veldwaarde zoeken? Medium is geen browsetag.
            if (!subtag) {
                ccUtils.customSearch(ctx, '/Record/' + fieldName, '', '' + value + '', label + ' : ' + value);
            } else {
                ccUtils.customSearch(ctx, '/Record/' + fieldName + '/' + subtag, '', '' + value + '', label + ' : ' + value);
            }
        };

        // If fieldValue doesn't exist and no defaultValue is provided, return null
        if (!fieldValue && !defaultValue) return null;

        return (
            <div className={styles.row}>
                <span className={styles.taglabel}>{label} : </span>
                {fieldValue
                    ? <span
                    className={styles.hyperlink}
                    onClick={(e) => handleOnClick(label, fieldValue, e)}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize((fieldValue || defaultValue).replace(/<break>/gi, '<br />'))
                    }}
                  ></span>                  
                    : defaultValue}
            </div>
        );
    }

    function getThesaurusRows(record: any, label: string, CN: string, minLength: number) {
        const thesTerms = record?.data?.Record?.ThesTerm;

        if (!thesTerms) return null;

        // Filter the terms based on the provided CN
        const terms = Array.isArray(thesTerms) ?
            thesTerms.filter((term: any) => term.CN.startsWith(CN)) :
            [thesTerms].filter((term: any) => term.CN.startsWith(CN));

        const rows = terms.map((term: any) => {
            let termElements: JSX.Element[] = [];
            let firstDone = false;

            term.TermPathPart.forEach((part: any) => {
                if (part.CN && part.CN.length >= minLength) {
                    termElements.push(
                        <span>
                            {firstDone && <span className={styles.thesNodeSeperator}>&gt;</span>}
                            <span className={styles.thesNode} onClick={(e) => handleThesaurusNodeClick(e, part.CN, label, part.TermPathPart)}>
                                {part.TermPathPart}
                            </span>
                        </span>
                    );
                    firstDone = true;
                }
            });

            return (
                <div className={styles.row} key={term.CN}>
                    <span className={styles.taglabel}>{label} : </span>
                    {termElements}
                </div>
            );
        });

        if (rows.length === 0) return null;

        return rows;
    }

    function getThesaurusRowsByType(record: any, label: string, ThesType: string, minLength: number) {
        const thesTerms = record?.data?.Record?.mqb_thesterm;
        if (!thesTerms) return null;

        const terms = Array.isArray(thesTerms)
            ? thesTerms.filter((term: any) => term.Type.trim() === ThesType)
            : [thesTerms].filter((term: any) => term.Type.trim() === ThesType);

        const rows = terms.map((term: any, idx) => {
            let termElements: JSX.Element[] = [];
            let firstDone = false;

            const processPart = (part: any) => {
                if (part.CN && part.CN.length >= minLength) {
                    termElements.push(
                        <span>
                            {firstDone && <span className={styles.thesNodeSeperator}>&gt;</span>}
                            <span className={styles.thesNode} onClick={(e) => handleThesaurusNodeClick(e, part.CN, label, part.TERM)}>
                                {part.TERM}
                            </span>
                        </span>
                    );
                    firstDone = true;
                }
            };

            if (minLength === -1) {
                processPart(term.thesTerms[term.thesTerms.length - 1]);
            } else {
                term.thesTerms.forEach(processPart);
            }

            let matchingRemarks = "";

            if (ThesType == "_Toponyme") { //20250404 WvW, _ added
                const termCN = term.CN;
                let toponymes = record?.data?.Record?.Toponyme;
                if (toponymes && !Array.isArray(toponymes)) {
                    toponymes = [toponymes];
                }

                if (toponymes && Array.isArray(toponymes)) {
                    const matchingToponyme = toponymes.find(t => t.CN === termCN && t.Remarks);
                    if (matchingToponyme) {
                        matchingRemarks = " [" + matchingToponyme.Remarks + "]";
                    }
                }
            }

            return (
                <div className={styles.row} key={`${term.CN}-${idx}`}>
                    <span className={styles.taglabel}>{label} : </span>
                    {termElements}{matchingRemarks}
                </div>
            );
        });

        if (rows.length === 0) return null;

        return rows;
    }

    function handleThesaurusNodeClick(event: React.MouseEvent, CN: string, label: string, term: string): void {
        event.stopPropagation();
        event.preventDefault();
        ccUtils.customSearch(ctx, '/Record/mqb_thesterm/thesTerms/CN', '', '[' + CN + ']', label + ': ' + term);
        //console.log(CN, term);
    }

    function hasFilterValue(record: any, filterField: string, filterValue: string): boolean {
        const fieldData = record?.data?.Record?.[filterField];

        if (!fieldData) return false;

        // If fieldData is an array
        if (Array.isArray(fieldData)) {
            return fieldData.includes(filterValue);
        }

        // If fieldData is a single value

        return fieldData === filterValue;
    }

    function getPersonnes(record: any, tag: keyof typeof record.data.Record) {
        const elements = record?.data?.Record?.[tag];
        if (!elements) return null;

        // Convert to array if not already
        const elementArray = Array.isArray(elements) ? elements : [elements];

        // Sorting logic (if required)
        elementArray.sort((a, b) => {
            const keyA = a.RoleTypeID + ',' + a.DisplayOrder;
            const keyB = b.RoleTypeID + ',' + b.DisplayOrder;
            return keyA.localeCompare(keyB);
        });

        const handleOnClick = (e: React.MouseEvent, name: string) => {
            e.preventDefault();
            e.stopPropagation();
            ccUtils.customSearch(ctx, '/Record/ConX/AlphaSort,/Record/ConXother/AlphaSort', '', '[' + name + ']', t('constituent')+' : ' + name);
        };

        const rows = elementArray.map((element: any, index) => {
            if (!(tag == "ConXother" && (element.RoleTypeID == 2 || element.RoleTypeID == 5))) {
                const { Role, [tag]: tagValue, DisplayDate } = element;
                return (
                    <div className={styles.row} key={index}>
                        <span className={styles.taglabel}>{Role} : </span>
                        <span className={styles.authName} onClick={(e) => handleOnClick(e, tagValue)}>{tagValue}</span>
                        {DisplayDate && <span className={styles.authDate}>&nbsp;({DisplayDate})</span>}
                    </div>
                );
            }
        });

        if (rows.length === 0) return null;

        return rows;
    }

    function getPersonnesWithRoleType(record: any, tag: keyof typeof record.data.Record, roleTypes: string[]) {
        const elements = record?.data?.Record?.[tag];
        if (!elements) return null;

        const elementArray = Array.isArray(elements) ? elements : [elements];

        elementArray.sort((a, b) => {
            const keyA = a.RoleTypeID + ',' + a.DisplayOrder;
            const keyB = b.RoleTypeID + ',' + b.DisplayOrder;
            return keyA.localeCompare(keyB);
        });

        const handleOnClick = (e: React.MouseEvent, name: string) => {
            e.preventDefault();
            e.stopPropagation();
            ccUtils.customSearch(ctx, '/Record/ConX/AlphaSortConX,/Record/ConXother/AlphaSort', '', '[' + name + ']', t('constituent')+' : ' + name);
        };

        const rows = elementArray.map((element: any, idx) => {
            if (roleTypes.includes(element.RoleTypeID)) {
                const { Role, AlphaSort, AlphaSortConX, DisplayDate } = element;
                return (
                    <div className={styles.row} key={`${element.ConstituentID}-${idx}`} >
                        <span className={styles.taglabel}>{Role} : </span>
                        <span className={styles.authName} onClick={(e) => handleOnClick(e, AlphaSort ? AlphaSort : AlphaSortConX)}>{AlphaSort ? AlphaSort : AlphaSortConX}</span>
                        {DisplayDate && <span className={styles.authDate}>&nbsp;({DisplayDate})</span>}
                        { }
                    </div>
                );
            }
        });

        if (rows.length === 0) return null;

        return rows;
    }


    function getObjectTitle(record: any) {

        const titleData = record?.data?.Record?.Title;

        if (!titleData) return <div className={styles.row}>{t('No title')}</div>;

        // Check if titleData is an array and handle accordingly
        if (Array.isArray(titleData)) {
            return titleData.map((data, index) => {
                const title = data.Title;
                const titleType = data.TitleType;

                if (!title) return null; // Skip rendering if title is not present

                return (
                    <div key={index} className={styles.row}>
                        {titleType ? (
                            <>
                                <span className={styles.taglabel}>{titleType} :</span> {title}
                            </>
                        ) : title}
                    </div>
                );
            });
        } else {
            const title = titleData.Title;
            const titleType = titleData.TitleType;

            if (!title) return <div className={styles.row}>{t('No title')}</div>;

            return (
                <div className={styles.row}>
                    {titleType ? (
                        <>
                            <span className={styles.taglabel}>{titleType} :</span> {title}
                        </>
                    ) : title}
                </div>
            );
        }
    }


    function getIconoTitle(record: any) {

        const titleData = record?.data?.Record?.Title;

        if (!titleData) return <div className={styles.row}>{t('No title')}</div>;

        // Check if titleData is an array and handle accordingly
        if (Array.isArray(titleData)) {
            return (
                <>
                    {titleData.map((data, index) => {
                        const title = data.Title;
                        const titleType = "Historical title";

                        if (!title) return null; // Skip rendering if title is not present

                        return (
                            <div key={index} className={styles.row}>
                                <span className={styles.taglabel}>{t(titleType)} :</span> {title}
                            </div>
                        );
                    })}
                </>
            );
        } else {
            const title = titleData.Title;
            const titleType = "Historical title";

            if (!title) return <div className={styles.row}>{t('No title')}</div>;

            return (
                <div className={styles.row}>
                    <span className={styles.taglabel}>{t(titleType)} :</span> {title}
                </div>
            );
        }
    }

    let imageUrl = ""; //todo: noimage
    const allImages = getImages(record);
    if ((allImages as any)["list"]?.length > 0) {
        imageUrl = (allImages as any)["list"][0]?.url;
    }
    console.log(imageUrl)

    const invNo = record?.data?.Record?.ObjectNumber;
    const recordDataAsString = JSON.stringify(record?.data?.Record, null, 2); // Converts the object to a JSON string with pretty print

    return (
        <div>
            {false && <textarea value={recordDataAsString}></textarea>}

            <a
                href={`${window.location.pathname}?action=search&field=/Record/ObjectNumber&label=N°%20de%20gestion&value=${invNo}`}
                className={styles.RecordList}
                key={index}
                onClick={(event) => handleLinkClick(event, index)}
                onMouseDown={e => e.preventDefault()}
            >
                <div className={styles.listViewPanel}>
                    <div className={styles.ccListviewRecordTable}>
                        <div className={styles.ccListviewRecordImage}>
                            { /* cc/imageproxy.ashx?server=localhost&port=15012&filename=noimage/pas-dimage-white.png&width=220&borderwidth=0&borderheight=0&bordercolor=000000&bg=ffffff&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=ff6666&cache=yes */}
                            {imageUrl && <ImageWithFallback src={imageUrl} alt="Image" placeholder="ccImageProxy.ashx?filename=noimage/pas-dimage-white.png&width=220" />}
                        </div>
                        <div className={styles.ccListviewRecordText}>

                            {

                                record?.data?.Record?.source === "objects" &&
                                <>
                                    {getPersonnesWithRoleType(record, 'ConX', ['1'])}
                                    {getPersonnesWithRoleType(record, 'ConXother', ['1'])}

                                    {/* getDefaultRow(record, "Titre", "Title", 'No title') */}
                                    {getObjectTitle(record)}
                                    {getDefaultRow(record, t("record_inventory_number"), "ObjectNumber")}

                                    {getDefaultRow(record, "Nom vernaculaire", "Record/NomLocal") /* Used nowhere? */}
                                    {getThesaurusRowsByType(record, "Type d'objet", 'musique', 7) /* Used nowhere? */}
                                    {getThesaurusRowsByType(record, "Type d'objet", 'textiles', 7) /* Used nowhere? */}
                                    {getThesaurusRowsByType(record, "Thèmes iconographiques", 'themes', 7) /* Used nowhere? */}
                                    {(hasFilterValue(record, 'filter', 'expose') || hasFilterValue(record, 'filter', 'histoire')) && getDefaultRow(record, "Type d'objet", "Record/Classification")}

                                    {getThesaurusRowsByType(record, t("record_culture"), 'Ethnonyme / Style', 7)}
                                    {getThesaurusRowsByType(record, t("record_place"), '_Toponyme', 7)}

                                    {getDefaultRow(record, "Religion", "Religion") /* Used nowhere? */}


                                    {getPersonnesWithRoleType(record, 'ConXother', ['2'])}
                                    {/* getPersonnesWithRoleType(record, 'ConXother', ['5']) not according 230925_Evolutions-pod19_Recette_WvW_20231217.docx */}
                                    {getDefaultRow(record, t("record_dates"), "Dated")}

                                    {getDefaultRowHyperlink(record, "Culture", "Culture", "Culture")  /* Used nowhere? */}

                                    {getDefaultRowHyperlinkHtml(record, t("record_materials_techniques"), "Medium")}
                                    {getThesaurusRowsByType(record, "Techniques", 'Technique', 7) /* Used nowhere? */}
                                    {/* getDefaultRow(record, "Dimensions", "Dimensions") */}
                                    {/* (hasFilterValue(record, 'filter', 'expose')) && getDefaultRow(record, "Exposé", "Record/emplacement") */}
                                    {(hasFilterValue(record, 'Expose', 'Oui')) && getDefaultRow(record, t("record_exhibited_object"), "ObjLocation.LocationString")}

                                    {/* getDefaultRow(record, "Validation", "ValidationConservateur") */}

                                </>
                            }

                            {
                                record?.data?.Record?.source === "icono" &&
                                <>
                                    {getPersonnesWithRoleType(record, 'ConX', ['1'])}
                                    {getPersonnesWithRoleType(record, 'ConXother', ['1'])}

                                    {getIconoTitle(record)}
                                    {/* getDefaultRow(record, "Titre", "Title", 'No title') */}
                                    {getDefaultRow(record, t("record_inventory_number"), "ObjectNumber")}
                                    {getDefaultRow(record, t("record_management_number"), "ObjectNumber2")}
                                    {getThesaurusRowsByType(record, "Culture", 'Populations', 7) /* Used nowhere? */}
                                    {getThesaurusRowsByType(record, t("record_place"), '_Toponyme', 7)}
                                    {getThesaurusRowsByType(record, "Thèmes iconographiques", 'themesico', 7) /* Used nowhere? */}
                                    {getThesaurusRowsByType(record, "Thèmes iconographiques", 'themes2ico', 7) /* Used nowhere? */}
                                    {getThesaurusRowsByType(record, "Procédés graphiques", 'procedesico', 7) /* Used nowhere? */}
                                    {getPersonnesWithRoleType(record, 'ConXother', ['2'])}
                                    {/* getPersonnesWithRoleType(record, 'ConXother', ['5']) not according 230925_Evolutions-pod19_Recette_WvW_20231217.docx */}
                                    {getDefaultRow(record, t("record_dates"), "Dated")}
                                    {/* getDefaultRow(record, "Evénement", "Evenement") */}
                                    {getDefaultRow(record, "Sujets", "Sujets") /* Used nowhere? */}
                                    {getDefaultRowHyperlink(record, t("record_materials_techniques"), "Medium")}
                                    {/* getDefaultRow(record, "Dimensions", "Dimensions") */}
                                    {getDefaultRow(record, "Vue associée", "Assoc") /* Used nowhere? */}

                                </>
                            }

                        </div>
                    </div>
                </div>
            </a>
        </div>
    );
}

export default RecordList;
